import { HttpInterceptorFn } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

import { WebSocketService } from '../shared/websocket.service';
import { VbLxpAuthService } from './vb-lxp-auth.service';

export interface MvAuthInterceptorConfig {
	/**
	 * true: attach token to all /api/* calls (private apps)
	 * false: attach token only to /api/admin/* calls (public apps with protected admin)
	 */
	requiresAuth: boolean;
}

export const MV_AUTH_INTERCEPTOR_CONFIG = new InjectionToken<MvAuthInterceptorConfig>(
	'MV_AUTH_INTERCEPTOR_CONFIG',
	{
		providedIn: 'root',
		factory: () => ({ requiresAuth: false })
	}
);

export const mvAuthInterceptor: HttpInterceptorFn = (req, next) => {
	const router = inject(Router);
	const webSocketService = inject(WebSocketService, { optional: true });
	const interceptorConfig = inject(MV_AUTH_INTERCEPTOR_CONFIG);
	const authService = inject(VbLxpAuthService);

	const isApiRequest = req.url.includes('/api/');
	const isAdminApiRequest = req.url.includes('/api/admin/');
	const shouldAttachToken = interceptorConfig.requiresAuth ? isApiRequest : isAdminApiRequest;

	if (!shouldAttachToken) {
		return next(req);
	}

	const token = authService.getToken();

	if (!token) {
		// No token available, let the request proceed (backend will handle optionalAuth)
		return next(req);
	}

	// Clone the request and add the authorization header
	const authReq = req.clone({
		setHeaders: {
			Authorization: `Bearer ${token}`,
		},
	});

	return next(authReq).pipe(
		catchError((error) => {
			// Handle 401 errors globally
			if (error.status === 401) {
				authService.clearToken();
				// Disconnect SSE only in fully authenticated apps.
				if (interceptorConfig.requiresAuth && webSocketService) {
					webSocketService.disconnect();
				}
				router.navigate(['/login']);
			}
			return throwError(() => error);
		}),
	);
};
